import { AddCircleOutlined, DeleteOutlined } from "@mui/icons-material";
import { Box, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useCreateIncentiveConfiguration, useUpdateIncentiveConfiguration } from "../../../../hooks/useIncentiveConfigurations";
import type { IncentiveConfiguration, IncentiveConfigurationWrite, IncentivePlanWrite } from "../../../../utils/models";

type WindowRow = { id: string; startHour: string; endHour: string; value: string };

// First window defaults to start=0 so Ops doesn't have to type it.
// Stable per-row ids let React track rows across add/remove without shuffling DOM state into the wrong row.
const makeFirstRow = (): WindowRow => ({ id: uuidv4(), startHour: "0", endHour: "", value: "" });
const blankRowStartingAt = (startHour: string): WindowRow => ({ id: uuidv4(), startHour, endHour: "", value: "" });

const toRow = (start: number, end: number | null | undefined, value: number): WindowRow => ({
	id: uuidv4(),
	startHour: String(start),
	endHour: end == null ? "" : String(end),
	value: String(value),
});

const toPlan = (row: WindowRow): IncentivePlanWrite => ({
	startHour: Number(row.startHour),
	endHour: row.endHour === "" ? null : Number(row.endHour),
	value: Number(row.value),
});

// Percent change vs. the previous window's bonus. Null when either value is blank/non-numeric or prev is 0
// (can't compute percent against a zero baseline).
const percentChangeFromPrev = (prevValue: string, currentValue: string): number | null => {
	if (prevValue === "" || currentValue === "") return null;
	const prev = Number(prevValue);
	const curr = Number(currentValue);
	if (Number.isNaN(prev) || Number.isNaN(curr) || prev === 0) return null;
	return Math.round(((curr - prev) / prev) * 100);
};

type ValidationResult = { ok: true } | { ok: false; reason: string };

// Mirrors `_validate_plan_windows` in backend/services/incentive_calculation.py (the source of truth). Keep in sync.
const validate = (name: string, rows: WindowRow[], t: (key: string) => string): ValidationResult => {
	if (!name.trim()) return { ok: false, reason: t("incentive.plan.name.empty") };
	if (rows.length === 0) return { ok: false, reason: t("incentive.plan.windows.empty") };

	// Reject blank startHour/value before parsing: Number("") === 0, which would silently pass the
	// `>= 0` / `start === 0` checks below. Blank endHour is intentional (means open-ended).
	for (const r of rows) {
		if (r.startHour === "" || r.value === "") return { ok: false, reason: t("incentive.plan.window.range.invalid") };
	}

	const parsed = rows.map((r, idx) => ({ idx, start: Number(r.startHour), end: r.endHour === "" ? null : Number(r.endHour), value: Number(r.value) }));

	for (const p of parsed) {
		if (Number.isNaN(p.start) || Number.isNaN(p.value) || (p.end !== null && Number.isNaN(p.end)))
			return { ok: false, reason: t("incentive.plan.window.range.invalid") };
		if (p.value < 0) return { ok: false, reason: t("incentive.plan.window.value.invalid") };
		if (p.start < 0) return { ok: false, reason: t("incentive.plan.window.range.invalid") };
		if (p.end !== null && p.end <= p.start) return { ok: false, reason: t("incentive.plan.window.range.invalid") };
	}

	const sorted = [...parsed].sort((a, b) => a.start - b.start);
	if (sorted[0].start !== 0) return { ok: false, reason: t("incentive.plan.window.first.must.start.at.zero") };
	for (let i = 0; i < sorted.length; i++) {
		const isLast = i === sorted.length - 1;
		if (sorted[i].end === null && !isLast) return { ok: false, reason: t("incentive.plan.window.range.invalid") };
		if (i > 0) {
			const prev = sorted[i - 1];
			// Contiguous-only: each window must start exactly where the previous one ended.
			if (prev.end !== null && sorted[i].start !== prev.end) return { ok: false, reason: t("incentive.plan.window.not.contiguous") };
		}
	}
	return { ok: true };
};

type Props = {
	open: boolean;
	onClose: () => void;
	departmentId: string;
	configuration?: IncentiveConfiguration;
};

const BonusPlanDialog: FC<Props> = ({ open, onClose, departmentId, configuration }) => {
	const { t } = useTranslation();
	const isEdit = !!configuration;

	const [name, setName] = useState("");
	const [rows, setRows] = useState<WindowRow[]>([makeFirstRow()]);

	const createMutation = useCreateIncentiveConfiguration(departmentId);
	const updateMutation = useUpdateIncentiveConfiguration(departmentId);
	const isPending = createMutation.isPending || updateMutation.isPending;

	useEffect(() => {
		if (!open) return;
		if (configuration) {
			setName(configuration.name);
			setRows(configuration.plans.length > 0 ? configuration.plans.map((p) => toRow(p.startHour, p.endHour, p.value)) : [makeFirstRow()]);
		} else {
			setName("");
			setRows([makeFirstRow()]);
		}
	}, [open, configuration]);

	const validation = useMemo(() => validate(name, rows, t), [name, rows, t]);

	const updateRow = (index: number, patch: Partial<WindowRow>) => {
		setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
	};

	const addRow = () =>
		setRows((prev) => {
			// New row's Start auto-fills with the previous row's End so windows stay contiguous out of the box.
			const last = prev[prev.length - 1];
			return [...prev, blankRowStartingAt(last?.endHour ?? "")];
		});
	const removeRow = (index: number) => setRows((prev) => prev.filter((_, i) => i !== index));

	const handleConfirm = async () => {
		if (!validation.ok) return;

		const body: IncentiveConfigurationWrite = {
			departmentId,
			name: name.trim(),
			plans: rows.map(toPlan),
		};

		try {
			if (isEdit && configuration) {
				await updateMutation.mutateAsync({ id: configuration.id, body });
			} else {
				await createMutation.mutateAsync(body);
			}
			onClose();
		} catch {
			// Mutation's onError already surfaced the failure via the error snackbar and analytics.
			// Keep the dialog open so the user can retry without re-entering their work.
		}
	};

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={isEdit ? t("incentive.edit.plan") : t("incentive.add.plan")}
			confirm={t("save")}
			cancel={t("cancel")}
			confirmDisabled={!validation.ok}
			confirmTooltip={validation.ok ? undefined : validation.reason}
			confirmLoading={isPending}
			onConfirm={handleConfirm}
			dataTestId="bonus-plan-dialog"
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 480 }}>
					<TextField
						label={t("name")}
						value={name}
						onChange={(e) => setName(e.target.value)}
						size="small"
						fullWidth
						autoFocus
						autoComplete="off"
						slotProps={{ htmlInput: { "data-testid": "bonus-plan-name-input" } }}
					/>

					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<Typography variant="subtitle2" sx={{ color: "primary.dark", fontWeight: 600 }}>
							{t("incentive.windows")}
						</Typography>
						<Typography variant="caption" sx={{ color: "text.secondary" }}>
							{t("incentive.windows.helper")}
						</Typography>

						{rows.map((row, index) => {
							const deltaPercent = index > 0 ? percentChangeFromPrev(rows[index - 1].value, row.value) : null;
							return (
								<Box key={row.id} sx={{ display: "flex", gap: 1, alignItems: "center" }} data-testid={`bonus-plan-window-row-${index}`}>
									<TextField
										label={t("incentive.window.start")}
										type="number"
										size="small"
										value={row.startHour}
										onChange={(e) => updateRow(index, { startHour: e.target.value })}
										autoComplete="off"
										slotProps={{
											input: { endAdornment: <InputAdornment position="end">h</InputAdornment> },
											htmlInput: { min: 0, "data-testid": `bonus-plan-window-start-${index}` },
										}}
										sx={{ width: 110 }}
									/>
									<TextField
										label={t("incentive.window.end")}
										type="number"
										size="small"
										value={row.endHour}
										onChange={(e) => updateRow(index, { endHour: e.target.value })}
										placeholder={t("incentive.window.end.open")}
										autoComplete="off"
										slotProps={{
											input: { endAdornment: <InputAdornment position="end">h</InputAdornment> },
											htmlInput: { min: 0, "data-testid": `bonus-plan-window-end-${index}` },
										}}
										sx={{ width: 130 }}
									/>
									<TextField
										label={t("incentive.window.value")}
										type="number"
										size="small"
										value={row.value}
										onChange={(e) => updateRow(index, { value: e.target.value })}
										autoComplete="off"
										slotProps={{
											input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
											htmlInput: { min: 0, step: 1, "data-testid": `bonus-plan-window-value-${index}` },
										}}
										sx={{ width: 110 }}
									/>
									<IconButton
										onClick={() => removeRow(index)}
										disabled={rows.length === 1}
										size="small"
										aria-label={t("delete")}
										data-testid={`bonus-plan-window-remove-${index}`}
									>
										<DeleteOutlined fontSize="small" />
									</IconButton>
									{deltaPercent !== null && (
										<Typography
											variant="caption"
											sx={{ minWidth: 48, fontWeight: 600, color: deltaPercent < 0 ? "error.main" : deltaPercent > 0 ? "success.main" : "text.secondary" }}
											data-testid={`bonus-plan-window-delta-${index}`}
										>
											{deltaPercent > 0 ? "+" : ""}
											{deltaPercent}%
										</Typography>
									)}
								</Box>
							);
						})}

						<SystemButton
							onClick={addRow}
							buttonVariant="secondary"
							size="small"
							leadingIcon={<AddCircleOutlined />}
							sx={{ alignSelf: "flex-start", mt: 1 }}
							data-testid="bonus-plan-window-add"
						>
							{t("incentive.window.add")}
						</SystemButton>
					</Box>
				</Box>
			}
		/>
	);
};

export default BonusPlanDialog;
